import { HttpClient } from '@adapters/HttpClient';
import { Client } from '../models/Clients';

export interface ClientsFilters {
  searchFilter?: { filters: any[]; logic: string };
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  /** Filtrar hijos del equipo padre (GET /teams-child?parent_team_id=...) */
  parent_team_id?: number;
  /** Búsqueda por nombre (GET /teams-child?name=...) */
  name?: string;
  /** Búsqueda por nombre del cliente, nombre o email del owner (GET /teams-child?search=...) */
  search?: string;
  country?: string;
  verified?: boolean;
  type?: string;
  /** Filtrar por origen del cliente (GET /teams-child?customer_origins=organic,affiliate) */
  customer_origins?: string[];
  /** Filtrar por IDs de team (GET /teams-child?team_ids=1,2,3) */
  team_ids?: number[];
  /** Filtrar por comercial asignado (GET /teams-child?commercial_ids=7,9) */
  commercial_ids?: number[];
  /** Incluir los clientes sin comercial asignado (OR con commercial_ids) */
  commercial_unassigned?: boolean;
  /** Filtrar por tipo de pedido (GET /teams-child?order_types=cod,hybrid) */
  order_types?: string[];
  /** Filtrar por idioma de marketing (GET /teams-child?marketing_langs=ES,EN) */
  marketing_langs?: string[];
}

export interface ClientsPage {
  clients: Client[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

function buildAdvancedFiltersParams(searchFilter: { filters: any[]; logic: string }) {
  const params: Record<string, any> = {};
  if (searchFilter.filters && Array.isArray(searchFilter.filters)) {
    searchFilter.filters.forEach((f, i) => {
      params[`advanced_filters[filters][${i}][column]`] = f.column;
      params[`advanced_filters[filters][${i}][operator]`] = f.operator;
      if (f.value !== undefined && f.value !== null && !(Array.isArray(f.value) && f.value.length === 0)) {
        params[`advanced_filters[filters][${i}][value]`] = f.value;
      }
    });
  }
  if (searchFilter.logic) {
    params['advanced_filters[logic]'] = searchFilter.logic;
  }
  return params;
}

export class ClientsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getClientsPaginated(filters: ClientsFilters = {}): Promise<ClientsPage> {
    const {
      searchFilter,
      sortBy = 'id',
      sortDirection = 'desc',
      page = 1,
      limit = 20,
      parent_team_id,
      name,
      search,
      country,
      verified,
      type,
      customer_origins,
      team_ids,
      commercial_ids,
      commercial_unassigned,
      order_types,
      marketing_langs,
    } = filters;

    const params: any = {
      ...(sortBy && { 'sorting[column]': sortBy }),
      ...(sortDirection && { 'sorting[direction]': sortDirection }),
      page,
      per_page: limit,
      ...(parent_team_id !== undefined && { parent_team_id }),
      ...(name !== undefined && name.trim() !== '' && { name: name.trim() }),
      ...(search !== undefined && search.trim() !== '' && { search: search.trim() }),
      ...(country && { country }),
      ...(verified !== undefined && { verified }),
      ...(type && { type }),
      ...(customer_origins &&
        customer_origins.length > 0 && {
          customer_origins: customer_origins.join(','),
        }),
      ...(team_ids && team_ids.length > 0 && { team_ids: team_ids.join(',') }),
      ...(commercial_ids && commercial_ids.length > 0 && { commercial_ids: commercial_ids.join(',') }),
      ...(commercial_unassigned && { commercial_unassigned: 1 }),
      ...(order_types && order_types.length > 0 && { order_types: order_types.join(',') }),
      ...(marketing_langs && marketing_langs.length > 0 && { marketing_langs: marketing_langs.join(',') }),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
    };

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>('/teams-child', params, {}, showGlobalLoading);

    return {
      clients: response.data,
      nextPage: response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  /**
   * GET /api/v1/teams-child/commercials
   *
   * Comerciales asignados a algún cliente, aunque ya no sean miembros del team
   * (por eso no sirve `GET /users`). Paginado y con búsqueda por nombre.
   */
  async getCommercialsPaginated({
    search,
    page = 1,
    limit = 20,
  }: {
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<{ commercials: { id: number; name: string }[]; hasMore: boolean }> {
    const response = await this.httpClient.get<any>('/teams-child/commercials', {
      page,
      per_page: limit,
      ...(search?.trim() && { search: search.trim() }),
    });

    return {
      commercials: response.data ?? [],
      hasMore: response.meta.currentPage < response.meta.lastPage,
    };
  }

  async getAll(): Promise<Client[]> {
    const response = await this.getClientsPaginated();
    return response.clients;
  }

  /**
   * POST /api/v1/teams-child/export
   *
   * Body: los mismos filtros que el listado, sin paginación.
   * Returns: { url_file: string }
   */
  async exportClients(filters: ClientsFilters = {}): Promise<{ url_file: string }> {
    const {
      name,
      search,
      customer_origins,
      commercial_ids,
      commercial_unassigned,
      order_types,
      marketing_langs,
      team_ids,
    } = filters;

    const body: Record<string, unknown> = {
      ...(name !== undefined && name.trim() !== '' && { name: name.trim() }),
      ...(search !== undefined && search.trim() !== '' && { search: search.trim() }),
      ...(customer_origins && customer_origins.length > 0 && { customer_origins: customer_origins.join(',') }),
      ...(commercial_ids && commercial_ids.length > 0 && { commercial_ids: commercial_ids.join(',') }),
      ...(commercial_unassigned && { commercial_unassigned: true }),
      ...(order_types && order_types.length > 0 && { order_types: order_types.join(',') }),
      ...(marketing_langs && marketing_langs.length > 0 && { marketing_langs: marketing_langs.join(',') }),
      ...(team_ids && team_ids.length > 0 && { team_ids: team_ids.join(',') }),
    };

    return this.httpClient.post<{ url_file: string }>('/teams-child/export', body);
  }

  async getClientById(id: number): Promise<Client> {
    const response = await this.httpClient.get<Client>(`/teams-child/${id}`);
    return response;
  }
}
